/**
 * Cloudinary URL utilities - optimización y watermarks
 *
 * Watermark opcional: solo si existe en tu cuenta Cloudinary.
 * Definí `VITE_CLOUDINARY_WATERMARK_ID` en .env / Vercel (no hay fallback en código: un public_id
 * borrado en Cloudinary rompe todas las URLs con overlay con 400).
 *
 * Opciones:
 * 1. public_id: VITE_CLOUDINARY_WATERMARK_ID=watermark-{brand}
 * 2. URL completa: VITE_CLOUDINARY_WATERMARK_ID=https://res.cloudinary.com/TU_CLOUD/image/upload/...
 *
 * @see https://cloudinary.com/documentation/image_transformations#apply_an_image_overlay
 *
 * Plan B: el Worker inyecta cloudinary* en `window.__EDGE_RUNTIME_CONFIG__`.
 */
import { edgeRuntimeString } from "@/lib/edgeRuntimeConfig";
import { BRAND_CLOUDINARY_FOLDER } from "./brandConfig";

function resolveWatermarkSource(): string {
  return (
    edgeRuntimeString("cloudinaryWatermarkId") ||
    (import.meta.env.VITE_CLOUDINARY_WATERMARK_ID as string) ||
    ""
  ).trim();
}

function resolveCloudName(): string {
  return (
    edgeRuntimeString("cloudinaryCloudName") ||
    (import.meta.env.VITE_CLOUDINARY_CLOUD_NAME as string)?.trim() ||
    ""
  );
}

function resolveCloudinaryBaseFolder(): string {
  return (
    edgeRuntimeString("cloudinaryBaseFolder") ||
    (import.meta.env.VITE_CLOUDINARY_BASE_FOLDER as string)?.trim() ||
    BRAND_CLOUDINARY_FOLDER
  );
}

export const isCloudinaryWatermarkConfigured = (): boolean =>
  resolveWatermarkSource().length > 0;

/** Cloudinary upload preset (unsigned browser upload). Worker → edge config → Vite env. */
export function getCloudinaryUploadPreset(): string {
  return (
    edgeRuntimeString("cloudinaryUploadPreset") ||
    (import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET as string)?.trim() ||
    ""
  );
}

/** Slug para carpeta Cloudinary (ej: "Campaign Look" → "campaign-look") */
export function slugifyCloudinaryFolder(title: string): string {
  return title
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 50) || "seccion";
}

/** Carpeta de subida para una sección del portfolio: `{brand}/portfolio/{slug-sección}`. */
export function portfolioSectionCloudinaryFolder(sectionTitle: string): string {
  const base = resolveCloudinaryBaseFolder();
  return `${base}/portfolio/${slugifyCloudinaryFolder(sectionTitle)}`;
}

/**
 * Carpeta destino en Cloudinary para uploads del admin.
 * @param subfolder — segmento fijo (ej. `portfolio`, `header`, `banner`)
 * @param leaf — nombre de sección o subcarpeta final (se slugifica)
 */
export function buildCloudinaryUploadFolder(
  subfolder: string | undefined,
  leaf: string | undefined,
): string {
  const base = resolveCloudinaryBaseFolder();
  const sub = subfolder?.trim() ? slugifyCloudinaryFolder(subfolder) : "";
  const leafSlug = leaf?.trim() ? slugifyCloudinaryFolder(leaf) : "";
  // Allow fixed subfolders without a leaf (e.g. mediakit → `{brand}/mediakit`)
  if (!leafSlug) {
    return sub ? `${base}/${sub}` : base;
  }
  if (sub) {
    return `${base}/${sub}/${leafSlug}`;
  }
  return `${base}/${leafSlug}`;
}

/**
 * Resuelve logo/header: acepta URL completa o public_id de Cloudinary.
 * Si es public_id (sin http), construye la URL con VITE_CLOUDINARY_CLOUD_NAME.
 */
export function resolveLogoUrl(value: string | undefined): string | undefined {
  if (!value?.trim()) return undefined;
  const v = value.trim();
  if (v.startsWith("http://") || v.startsWith("https://")) return v;
  const cloudName = resolveCloudName();
  if (!cloudName) return v;
  return `https://res.cloudinary.com/${cloudName}/image/upload/${v}`;
}

/**
 * Resuelve URL de imagen del portfolio. Si es public_id corto (sin /),
 * antepone la carpeta de la sección: {brand}/portfolio/{slug-título}/{id}
 */
export function resolvePortfolioImageUrl(
  value: string | undefined,
  sectionTitle?: string
): string | undefined {
  if (!value?.trim()) return undefined;
  const v = value.trim();
  if (v.startsWith("http://") || v.startsWith("https://")) return resolveLogoUrl(v);
  const cloudName = resolveCloudName();
  if (!cloudName) return v;
  // public_id corto (ej: 2_y6kdit) → anteponer carpeta de sección
  if (!v.includes("/") && sectionTitle) {
    const folder = portfolioSectionCloudinaryFolder(sectionTitle);
    return `https://res.cloudinary.com/${cloudName}/image/upload/${folder}/${v}`;
  }
  return `https://res.cloudinary.com/${cloudName}/image/upload/${v}`;
}

/** Indica si la URL es de Cloudflare Images (imagedelivery.net). No se usan en galería. */
export function isCloudflareImage(value: string | undefined): boolean {
  return !!value?.trim() && value.includes("imagedelivery.net");
}

/**
 * Indica si una URL o public_id es de Cloudinary.
 * URLs finales (workaround) deben funcionar sí o sí.
 */
export function isCloudinaryImage(value: string | undefined): boolean {
  if (!value?.trim()) return false;
  const v = value.trim();
  // URL final Cloudinary - cualquier formato que contenga res.cloudinary.com
  if (v.includes("res.cloudinary.com")) return true;
  if (v.startsWith("http://") || v.startsWith("https://")) return false;
  return !!resolveCloudName() && v.length > 0 && !v.includes(" ") && !v.includes("\n");
}

/**
 * Indica si el logo (URL o public_id) coincide con una URL de galería.
 * Usado para excluir el logo de la galería.
 */
export function isSameCloudinaryImage(
  logoValue: string | undefined,
  galleryUrl: string
): boolean {
  if (!logoValue?.trim() || !galleryUrl.includes("res.cloudinary.com"))
    return false;
  const logo = logoValue.trim();
  if (logo.startsWith("http")) {
    return galleryUrl.trim() === logo;
  }
  const parsed = parseCloudinaryUrl(galleryUrl);
  if (!parsed) return false;
  const logoPublicId = logo.replace(/\.[^.]+$/, "");
  return (
    parsed.publicId === logoPublicId ||
    parsed.publicId.endsWith("/" + logoPublicId)
  );
}

/**
 * Parámetros del watermark (opcional, valores por defecto)
 */
export interface WatermarkOptions {
  /** Posición: south_east (esquina inferior derecha), south_west, center, etc. */
  gravity?: string;
  /** Offset X en píxeles */
  x?: number;
  /** Offset Y en píxeles */
  y?: number;
  /** Ancho del watermark: 0.1-0.3 = 10-30% del ancho de la imagen */
  widthScale?: number;
  /** Opacidad 0-100 */
  opacity?: number;
}

const DEFAULT_WATERMARK: Required<WatermarkOptions> = {
  gravity: "south_east",
  x: 8,
  y: 8,
  widthScale: 0.08,
  opacity: 25,
};

/** Sin redimensionar la base antes del overlay, algunos JPG devuelven 400 en Cloudinary. */
const WATERMARK_BASE_FALLBACK = "w_1600,c_limit";

/** Detecta si es URL (http/https) */
function isWatermarkUrl(source: string): boolean {
  return source.startsWith("http://") || source.startsWith("https://");
}

/** Extrae cloud y public_id de una URL de Cloudinary */
function parseCloudinaryUrl(
  url: string
): { cloudName: string; publicId: string } | null {
  const m = url.match(
    /^https:\/\/res\.cloudinary\.com\/([^/]+)\/image\/upload\/(.+)$/
  );
  if (!m) return null;
  const [, cloudName, path] = m;
  const publicId = path.replace(/^v\d+\//, "").replace(/\.[^.]+$/, "");
  return { cloudName, publicId };
}

/**
 * Formato public_id para overlay: Cloudinary requiere / reemplazado por :
 * @see https://cloudinary.com/documentation/layers#image_overlays
 */
function formatPublicIdForOverlay(publicId: string): string {
  return publicId.replace(/\//g, ":");
}

/**
 * Base64 encode para URLs - compatible con Unicode.
 * Cloudinary requiere base64 con padding.
 */
function base64EncodeUrl(url: string): string {
  try {
    return btoa(url);
  } catch {
    return btoa(String.fromCharCode(...new TextEncoder().encode(url)));
  }
}

/**
 * Construye la cadena de overlay para Cloudinary
 * - public_id: l_<id> (misma cuenta, más fiable)
 * - URL Cloudinary misma cuenta: l_<public_id extraído>
 * - URL externa: l_fetch:<base64>
 */
function buildOverlayString(
  opts: WatermarkOptions | undefined,
  imageCloudName?: string
): string {
  const { gravity, x, y, widthScale, opacity } = {
    ...DEFAULT_WATERMARK,
    ...opts,
  };
  let layer: string;

  const watermarkSource = resolveWatermarkSource();
  if (isWatermarkUrl(watermarkSource)) {
    const parsed = parseCloudinaryUrl(watermarkSource);
    if (parsed && imageCloudName && parsed.cloudName === imageCloudName) {
      layer = `l_${formatPublicIdForOverlay(parsed.publicId)}`;
    } else {
      layer = `l_fetch:${base64EncodeUrl(watermarkSource)}`;
    }
  } else {
    layer = `l_${formatPublicIdForOverlay(watermarkSource)}`;
  }

  // Capa: escala relativa y opacidad; luego aplicar gravedad (doc: …/c_scale,fl_relative,w_0.5/o_50/fl_layer_apply…)
  return `${layer}/c_scale,fl_relative,w_${widthScale}/o_${opacity}/fl_layer_apply,g_${gravity},x_${x},y_${y}`;
}

/**
 * Aplica transformaciones a una URL de Cloudinary: optimización + watermark opcional
 *
 * El watermark se aplica si `VITE_CLOUDINARY_WATERMARK_ID` apunta a un asset existente.
 * Pasa watermark: false para desactivarlo en una imagen concreta.
 *
 * @param url     URL de Cloudinary completa
 * @param options.width     Ancho en px (usa c_fill para imágenes de galería, c_limit para portada)
 * @param options.fit       'fill' (crop) | 'limit' (resize sin crop). Default: 'limit'
 * @param options.height    Alto en px (solo si fit='fill')
 * @param options.watermark true (default) | false | WatermarkOptions con overrides
 * @param options.dpr       Device pixel ratio (1|2). Default: auto via dpr_auto
 */
export function getCloudinaryUrl(
  url: string,
  options?: {
    width?: number;
    height?: number;
    fit?: "fill" | "limit";
    watermark?: boolean | WatermarkOptions;
    dpr?: 1 | 2 | "auto";
  }
): string {
  if (!url.includes("res.cloudinary.com")) return url;

  const match = url.match(
    /^(https:\/\/res\.cloudinary\.com\/([^/]+)\/image\/upload\/)(.+)$/
  );
  if (!match) return url;

  const [, base, cloudName, path] = match;
  const parts: string[] = [];

  const applyWatermark =
    !!resolveWatermarkSource() && options?.watermark !== false;

  // ── Resize base ────────────────────────────────────────────────────────────
  if (options?.width || options?.height) {
    const fit = options.fit ?? "limit";
    const crop = fit === "fill" ? "c_fill" : "c_limit";
    const dims: string[] = [];
    if (options.width)  dims.push(`w_${options.width}`);
    if (options.height) dims.push(`h_${options.height}`);
    const dprPart = options?.dpr && options.dpr !== "auto" ? `,dpr_${options.dpr}.0` : "";
    parts.push(`${dims.join(",")},${crop}${dprPart}`);
  } else if (applyWatermark) {
    // Asegurar mínimo para que el overlay funcione sin errores 400
    parts.push(WATERMARK_BASE_FALLBACK);
  }

  // ── Watermark ──────────────────────────────────────────────────────────────
  if (applyWatermark) {
    const overlay =
      options?.watermark === true || options?.watermark === undefined
        ? buildOverlayString(undefined, cloudName)
        : buildOverlayString(
            options.watermark as WatermarkOptions,
            cloudName
          );
    parts.push(overlay);
  }

  // ── Calidad + formato auto ─────────────────────────────────────────────────
  // dpr_auto: Cloudinary elige DPR según el User-Agent (requiere fl_lossy para AVIF)
  const dprToken = options?.dpr === "auto" ? ",dpr_auto" : "";
  parts.push(`q_auto,f_auto${dprToken}`);

  const transform = parts.join("/");
  return `${base}${transform}/${path}`;
}

/**
 * Resuelve public_id a URL y aplica optimización + watermark.
 * Solo para imágenes Cloudinary. Para otras URLs retorna undefined (no mostrar).
 */
export function resolveAndOptimizeCloudinaryUrl(
  value: string | undefined,
  options?: { width?: number; height?: number; fit?: "fill" | "limit"; watermark?: boolean }
): string | undefined {
  if (!value?.trim() || !isCloudinaryImage(value)) return undefined;
  const resolved = resolveLogoUrl(value);
  if (!resolved?.includes("res.cloudinary.com")) return undefined;
  return getCloudinaryUrl(resolved, { ...options, watermark: options?.watermark !== false });
}

// ── Responsive helpers ────────────────────────────────────────────────────────

/**
 * Genera un srcset de Cloudinary para `<img srcset>` o `next/image`.
 * Solo para imágenes Cloudinary — retorna undefined para otras URLs.
 *
 * @param url       URL de Cloudinary
 * @param widths    Array de anchos en px (default: [400, 800, 1200, 1600])
 * @param options   Mismas opciones que getCloudinaryUrl (watermark, fit, etc.)
 *
 * @example
 * <img
 *   src={getCloudinaryUrl(url, { width: 800 })}
 *   srcSet={getCloudinarySrcSet(url) ?? undefined}
 *   sizes="(max-width: 768px) 100vw, 50vw"
 * />
 */
export function getCloudinarySrcSet(
  url: string,
  widths: number[] = [400, 800, 1200, 1600],
  options?: Omit<Parameters<typeof getCloudinaryUrl>[1], "width">
): string | undefined {
  if (!url.includes("res.cloudinary.com")) return undefined;
  return widths
    .map((w) => `${getCloudinaryUrl(url, { ...options, width: w, fit: "limit" })} ${w}w`)
    .join(", ");
}

/**
 * Retorna la URL de thumbnail Cloudinary para una imagen de portfolio.
 * Usa c_fill para mantener el aspect ratio uniforme en grillas.
 *
 * @param url       URL de Cloudinary
 * @param width     Ancho del thumbnail (default: 600)
 * @param height    Alto del thumbnail (default: 800 — portrait 3:4)
 * @param watermark Si aplicar watermark (default: true)
 */
export function getCloudinaryThumbnail(
  url: string,
  width = 600,
  height = 800,
  watermark = true,
): string {
  if (!url.includes("res.cloudinary.com")) return url;
  return getCloudinaryUrl(url, { width, height, fit: "fill", watermark });
}

/**
 * Retorna la URL de hero/banner Cloudinary: ancho máximo sin crop, auto calidad.
 * Sin watermark por defecto (hero images no se protegen).
 *
 * @param url   URL de Cloudinary
 * @param width Ancho máximo (default: 1600)
 */
export function getCloudinaryHero(url: string, width = 1600): string {
  if (!url.includes("res.cloudinary.com")) return url;
  return getCloudinaryUrl(url, { width, fit: "limit", watermark: false });
}
